import {
  useState,
  useMemo,
  useCallback,
  useEffect
 } from 'react';
import { useHistory, useLocation } from "react-router";
import { SelectedType } from '@atlaskit/tabs/types';
import { URL } from "../../constants/navigation";
import { MatchDatesAccordion } from "../../components/MatchDatesAccordion";
import { MaxMatchGroups } from "../../components/MaxMatchGroups";
import { RenewalToRenewalMatchingTable } from "../../components/RenewalToRenewalMatchingTable";
import { NewToNewMatchingTable } from "../../components/NewToNewMatchingTable";
import { PauseUnitTypeMatchingTable } from "../../components/PauseUnitTypeMatchingTab";
import { TablePaginationProvider } from "../../components/TablePagination/context";
import { useNetwork } from "../../hooks/useNetwork";
import { useSelectedNetworkImportTypes } from "../../hooks/useSelectedNetworkImportType";
import { MainLayout } from "../../layout/MainLayout";
import { Alert } from "../../UI/Alert";
import { Grid } from "../../UI/Grid";
import { Tabs } from "../../UI/Tabs";
import { TabList } from "../../UI/TabList";
import { Tab } from "../../UI/Tab";
import { TabPanel } from "../../UI/TabPanel";
import { Typography } from "../../UI/Typography";
import { Link } from "../../UI/Link";


const tabs = [
  "matching-round-dates",
  "pause-unit-type-matching",
  "max-groups",
  "renewal-to-renewal-matching",
  "new-to-new-matching",
  
];


const MATCH_GROUPS_DESCRIPTION = "This tool ensures that users without any roommates cannot match together once a specific number of roommate groups (2 or more users) has been reached. By implementing this tool, it effectively prevents the occurrence of having more roommate groups in RoomSync than there are available units in the community. To function properly, we recommend setting 'NO. OF MATCH GROUPS - MAX' to be equivalent to the total number of units (front doors, not beds) that exist at your community for each unit type.";
const PAUSE_UNIT_DESCRIPTION = "Pausing a unit type prevents residents from taking any matching action (requesting a match, accepting a request, leaving a match, swiping on potential roommates) even if matching round dates are still open. Residents will see the matching round as closed, the same as they would when matching round dates expire.";
const PAUSE_UNIT_DESCRIPTION_1 ="A unit type can be paused and unpaused when needed using the tool below.";

export const MatchRulesPage = () => {
  const [ selectedTab, setSelectedTab ] = useState(0);
  const { push } = useHistory();
  const location = useLocation();

  const { selectedNetwork } = useNetwork();
  const { isEntrata } = useSelectedNetworkImportTypes();

  const navTo = (url:string) => () => {
    push(url);
  };

  const handleUpdate = useCallback(
    (index: SelectedType) => {
      setSelectedTab(index);
      push(URL.MATCH_RULES_BASE + tabs[index]);
    },
    [setSelectedTab],
  );

  // Set initial tab based on URL
  useEffect(() => {
    const currentPath = location.pathname;
    const tabSegment = currentPath.replace(URL.MATCH_RULES_BASE, '');
    const tabIndex = tabs.indexOf(tabSegment);
    if (tabIndex !== -1) {
      setSelectedTab(tabIndex);
    }
  }, [location.pathname]);

  const RENEVAL_TO_RENEWAL_MATCHING_DESCRIPTION = useMemo(() => <>
    This rule restricts renewing residents from seeing or matching with other renewals. 
    It is meant to prevent administrators from receiving matches that result in unauthorized renewal transfers. 
    We recommend disabling renewal matching. If you enable or disable renewal matching for a unit type,
    it will only affect future users - RoomSync will not break up any existing matches. 
    Even if renewal matching is restricted, administrators can still match renewals together using.
    <Link onClick={navTo(URL.USERS_MATCH)}> Power Match</Link> if needed.
  </>, []);

  const NEW_TO_NEW_MATCHING_DESCRIPTION = useMemo(() => <>
    This rule restricts new residents from seeing or matching with other new residents. 
    It is meant to prevent administrators from receiving matches between new residents 
    when there are no available units to place them into. 
    If a new resident is already matched with a renewal resident, 
    we do not classify this group as a new resident group and they will still be visible. 
    This setting will only affect future users - RoomSync will not break up any existing matches. 
    Even if new matching is restricted, administrators can still match new residents together using.
    <Link onClick={navTo(URL.USERS_MATCH)}> Power Match</Link> if needed.
  </>, []);

  const isApartments = selectedNetwork.apt;


  return (
    <MainLayout title="Match Rules">
      
      <Tabs
        id="match-rules-tabs"
        onChange={handleUpdate}
        selected={selectedTab}
        >
            <TabList>
              <Tab>Matching Round Dates</Tab>
              <Tab>Pause Unit Type Matching</Tab>
              <Tab>Max Groups</Tab>
              {isApartments &&
                <Tab>Renewal to Renewal Matching</Tab>}
              {isApartments &&  
              <Tab>New to New Matching</Tab>}
            </TabList>
            <TabPanel>
              <Grid item xs={12} pt={2}>
                <TablePaginationProvider>
                  <MatchDatesAccordion />
                </TablePaginationProvider>
              </Grid>
            </TabPanel>
            <TabPanel>
                <Grid item xs={12} pt={2}>
                  <Typography variant="body2" mb={2}>
                    {PAUSE_UNIT_DESCRIPTION}
                  </Typography>
                  <Typography variant="body2" mb={2}>
                    {PAUSE_UNIT_DESCRIPTION_1}
                  </Typography>
                  <TablePaginationProvider>
                    <PauseUnitTypeMatchingTable />
                  </TablePaginationProvider>
                </Grid>
              </TabPanel>
            <TabPanel>
              <Grid item xs={12} pt={2}>
                <Typography variant="body2" mb={2}>
                  {MATCH_GROUPS_DESCRIPTION}
                </Typography>
                <TablePaginationProvider>
                  <MaxMatchGroups />
                </TablePaginationProvider>
              </Grid>
            </TabPanel>
            {isApartments &&
              <TabPanel>
                <Grid item xs={12} pt={2}>
                  <Typography variant="body2" mb={2}>
                    {RENEVAL_TO_RENEWAL_MATCHING_DESCRIPTION}
                  </Typography>
                  {isEntrata && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Residents who are Renewal Transfers in Entrata will show up as new residents in RoomSync.
                    </Alert>
                  )}
                  <TablePaginationProvider>
                    <RenewalToRenewalMatchingTable />
                  </TablePaginationProvider>
                </Grid>
              </TabPanel>
            }
            {isApartments &&
              <TabPanel>
                <Grid item xs={12} pt={2}>
                  <Typography variant="body2" mb={2}>
                    {NEW_TO_NEW_MATCHING_DESCRIPTION}
                  </Typography>
                  <TablePaginationProvider>
                    <NewToNewMatchingTable/>
                  </TablePaginationProvider>
                </Grid>
              </TabPanel>
            }
          </Tabs>
    </MainLayout>
  );
};
